import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'

import {
    getModelColumns,
    getModelFilterItems,
    getModelFilterToggles,
} from '../components/Models/model-list-config'
import { getItemModelsPaginated } from '../services'
import { paths } from '@/utils/path'

interface ModelListFilters {
    category: string
    is_active: string
}

const FILTER_DEFAULTS: ModelListFilters = {
    category: '',
    is_active: '',
}

export const useModelList = (itemType: 'equipment' | 'component') => {
    const navigate = useNavigate()

    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<ModelListFilters>({
        defaults: { sort_by: 'created_at', sort_order: 'desc' },
        filterDefaults: FILTER_DEFAULTS,
    })

    const { control } = useForm<ModelListFilters>({
        defaultValues: filters,
    })

    const fetchFn = useCallback(
        () => getItemModelsPaginated({ ...queryParams, item_type: itemType }),
        [queryParams, itemType]
    )

    const { data, refetch, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WAREHOUSE_MODELS, queryParams, itemType],
        queryFn: fetchFn,
        placeholderData: true,
    })

    const columns = useMemo(() => getModelColumns(), [])
    const filterItems = useMemo(() => getModelFilterItems(), [])
    const filterToggles = useMemo(() => getModelFilterToggles(), [])

    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )
    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    const handleCreate = useCallback(
        () => navigate(paths.warehouseModelCreate),
        [navigate]
    )

    return {
        models: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),

        control,
        setFilter,
        params,
        searchInputProps,

        columns,
        filterItems,
        filterToggles,

        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        handleCreate,
        refetch,
    }
}
